/** Admin console › Imports (`/admin/migrations`): every company's OpenSolar imports, newest first, with their
 * status, totals and the switch reward (approved by the run, or queued for staff on the organisation page). */
import { useQuery } from "@tanstack/react-query";
import { Import } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Card, Empty, PageHead, Select, Spinner } from "../../components/ui";
import { runChip } from "../../lib/migration";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";
import { useFmt } from "./format";

const REWARD: Record<S["MigrationRewardStatus"], { label: MessageKey; tone?: "ok" | "warn" | "info" }> = {
  approved: { label: "admin.migrations.rewardApproved", tone: "ok" },
  queued: { label: "admin.migrations.rewardQueued", tone: "warn" },
  already: { label: "admin.migrations.rewardAlready", tone: "info" },
  not_eligible: { label: "admin.migrations.rewardNotEligible" },
};

export function AdminMigrations() {
  const t = useT();
  const intl = useIntlShape();
  const { date, num } = useFmt();
  const [status, setStatus] = useState<"" | S["MigrationRunStatus"]>("");
  const [reward, setReward] = useState<"" | S["MigrationRewardStatus"]>("");
  const runs = useQuery({
    queryKey: ["admin-migrations", status, reward],
    queryFn: () => unwrap(api.GET("/v1/admin/migrations", { params: { query: { status: status || undefined, reward_status: reward || undefined } } })),
  });
  return (
    <>
      <PageHead title={t("admin.migrations.title")} description={t("admin.migrations.description")} />
      <Card pad={false}>
        <div className="toolbar">
          <div style={{ inlineSize: 190 }}>
            <Select
              value={status}
              onChange={setStatus}
              ariaLabel={t("admin.migrations.status")}
              options={[
                { value: "", label: t("admin.migrations.anyStatus") },
                ...(["previewed", "running", "waiting", "completed", "failed", "cancelled"] as const).map((value) => ({
                  value,
                  label: t("admin.migrations.runStatus", { status: value }),
                })),
              ]}
            />
          </div>
          <div style={{ inlineSize: 230 }}>
            <Select
              value={reward}
              onChange={setReward}
              ariaLabel={t("admin.migrations.rewardFilter")}
              options={[{ value: "", label: t("admin.migrations.anyReward") }, ...Object.entries(REWARD).map(([value, r]) => ({ value: value as S["MigrationRewardStatus"], label: t(r.label) }))]}
            />
          </div>
        </div>
        {runs.isLoading ? (
          <Spinner />
        ) : runs.error ? (
          <div className="card-body">
            <Alert tone="danger">{errorMessage(runs.error)}</Alert>
          </div>
        ) : !runs.data?.items.length ? (
          <Empty icon={<Import />} title={t("admin.migrations.empty")} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("admin.migrations.company")}</th>
                  <th>{t("admin.migrations.account")}</th>
                  <th>{t("admin.migrations.status")}</th>
                  <th className="num">{t("admin.migrations.projects")}</th>
                  <th className="num hide-sm">{t("admin.migrations.files")}</th>
                  <th>{t("admin.migrations.reward")}</th>
                  <th className="num hide-sm">{t("admin.migrations.started")}</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.items.map(({ organization_id, organization_name, run }) => {
                  const p = run.totals.projects;
                  const chip = runChip(run.status);
                  const r = run.reward ? REWARD[run.reward.status] : null;
                  return (
                    <tr key={run.id}>
                      <td>
                        <Link to={`/admin/orgs/${organization_id}`} dir="auto">{organization_name}</Link>
                      </td>
                      <td dir="auto">{run.source_account_name ?? "—"}</td>
                      <td>
                        <Badge tone={chip.tone}>{t("admin.migrations.runStatus", { status: run.status })}</Badge>
                        {run.error && <div className="cell-sub" dir="auto">{run.error}</div>}
                      </td>
                      <td className="num">
                        <div className="cell-main">{num(p.imported + p.partial + p.updated)}</div>
                        <div className="cell-sub">{t("admin.migrations.skippedFailed", { skipped: p.skipped, failed: p.failed })}</div>
                      </td>
                      <td className="num hide-sm">{num(run.totals.files.imported)}</td>
                      <td>
                        {r ? <Badge tone={r.tone}>{t(r.label)}</Badge> : <span className="faint">—</span>}
                        {run.reward && run.reward.status !== "approved" && (
                          <div className="cell-sub" dir="auto">{intl.formatList(run.reward.checks.filter((c) => !c.ok).map((c) => c.label), { type: "unit" })}</div>
                        )}
                      </td>
                      <td className="num hide-sm">{date(run.created_at, true)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
